import React, { useState, useEffect } from "react";
import {
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Grid,
  CircularProgress,
  Chip,
  OutlinedInput,
  FormHelperText,
  Paper,
  Tooltip,
  Radio,
  RadioGroup,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import ImageUploader from "components/shared/ImageUploder/ImageUploder";
import { AMITIES, ACCOMMODATION_TYPES, generateId, getS3FileUrl } from "utils/index";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { uploadMultipleImages } from "store/thunks/imageThunks";
import states from "utils/Data/state.json";
import CountryStateDistrict from "utils/Data";
import { getImage } from "services/createTripService";

type AccommodationType = (typeof ACCOMMODATION_TYPES)[number];

interface Image {
  file?: Blob | MediaSource | ArrayBuffer;
  preview: string;
  fileName?: string;
  mimetype?: string;
}

interface NightDetails {
  id: string;
  sameAs?: string;
  accomodationType: string;
  hotelName: string;
  district: string;
  state: string;
  amities: string[];
  hotelIntro: string;
  hotelImage: Image[];
}

interface StayDetailsModalProps {
  nights: number;
  isNotFinalized?: boolean;
  stayDetails?: NightDetails[];
  setStay: React.Dispatch<React.SetStateAction<NightDetails[]>>;
  setNoStay: React.Dispatch<React.SetStateAction<boolean>>;
  onSave?: (
    stayDetails: NightDetails[],
    isNotFinalized: boolean
  ) => Promise<void>;
  folderName: string;
  editable?: boolean;
  saving?: boolean;
}

const initialNightDetails = (id: string): NightDetails => ({
  id,
  accomodationType: "",
  hotelName: "",
  district: "",
  state: "",
  amities: [],
  hotelIntro: "",
  hotelImage: [],
});

const StayDetailsModal: React.FC<StayDetailsModalProps> = ({
  nights,
  isNotFinalized = false,
  stayDetails = [],
  setStay,
  setNoStay,
  onSave,
  folderName,
  editable,
  saving,
}) => {
  const [open, setOpen] = useState<boolean>(true);

  const [expanded, setExpanded] = useState<string | false>(false);
  const [configMode, setConfigMode] = useState<"addDetails" | "saveAs" | "">(
    "addDetails"
  );
  const dispatch = useDispatch();
  const [selectedNightSource, setSelectedNightSource] = useState<string>("");
  const [imageUploadLoading, setImageUploadLoading] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    if (open) {
      if (stayDetails.length > 0) {
        setStay(stayDetails);
      } else {
        setStay([]);
      }
      setNoStay(isNotFinalized);
    }
  }, [open]);

  const handleClose = (): void => {
    setOpen(false);
    setConfigMode("");
    setSelectedNightSource("");
    setExpanded(false);
  };

  const fetchAndStoreImageData = async (nightIndex: number) => {
    try {

      setImageUploadLoading((prev) => ({
        ...prev,
        [nightIndex]: true,
      }));

      const currentNight = stayDetails[nightIndex];
      if (currentNight.sameAs) {
        return;
      }
      if (currentNight?.hotelImage && currentNight.hotelImage.length > 0) {
        const processedImages = await Promise.all(
          currentNight.hotelImage.map(async (img) => {
            // Skip if image is null or already has file data
            if (img === null) return img;

            try {
              const buffer = await getImage(
                folderName,
                img?.fileName as string
              );

              return {
                ...img,
                file: buffer,
                fileName: img.fileName,
                preview: getS3FileUrl(folderName,img?.fileName as string)
              };
            } catch (imageError) {
              console.error(
                `Failed to fetch image ${img.fileName}:`,
                imageError
              );
              return img;
            }
          })
        );

        // Update the stay details with processed images
        setStay((prevStayDetails) => {
          const updatedStayDetails = [...prevStayDetails];
          updatedStayDetails[nightIndex] = {
            ...updatedStayDetails[nightIndex],
            hotelImage: processedImages.filter((img) => img !== null), 
          };
          return updatedStayDetails;
        });
      }
    } catch (error) {
      console.error("Error fetching image data:", error);
      toast.error("Failed to load images");
    } finally {
      setImageUploadLoading((prev) => ({
        ...prev,
        [nightIndex]: false,
      }));
    }
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      if (isExpanded) {
        // Extract night index from panel string (e.g., "panel0" -> 0)
        const nightIndex = parseInt(panel.replace("panel", ""));

        // Only fetch if we have a valid index and the night exists
        if (!isNaN(nightIndex) && stayDetails[nightIndex]) {
          fetchAndStoreImageData(nightIndex);
        }
      }
    };

  const handleAddNight = (): void => {
    if (stayDetails.length < nights) {
      const newId = generateId();
      const newStayDetails = [...stayDetails, initialNightDetails(newId)];
      setStay(newStayDetails);
      setExpanded(`panel${newStayDetails.length - 1}`);
    }
  };

  const handleRemoveNight = (index: number): void => {
    const removedNight = stayDetails[index];
    const newStayDetails = stayDetails.filter((_, idx) => idx !== index);

    // Update any nights that reference this removed night
    newStayDetails.forEach((night, i) => {
      if (night.sameAs === removedNight.id) {
        newStayDetails[i] = {
          ...night,
          sameAs: undefined,
        };
      }
    });

    setStay(newStayDetails);
  };

  const handleBreakLink = (index: number): void => {
    if (!stayDetails[index].sameAs) return;

    // Find the source night
    const sourceNight = stayDetails.find(
      (night) => night.id === stayDetails[index].sameAs
    );
    if (!sourceNight) return;

    // Create a copy without the sameAs link
    const updatedStayDetails = [...stayDetails];
    updatedStayDetails[index] = {
      ...updatedStayDetails[index],
      sameAs: undefined,
      accomodationType: sourceNight.accomodationType,
      hotelName: sourceNight.hotelName,
      district: sourceNight.district,
      state: sourceNight.state,
      amities: [...sourceNight.amities],
      hotelIntro: sourceNight.hotelIntro,
      hotelImage: [],
    };

    setStay(updatedStayDetails);
  };

  const handleTextFieldChange = (
    index: number,
    field: keyof NightDetails,
    value: string
  ): void => {
    const updatedStayDetails = [...stayDetails];
    updatedStayDetails[index] = {
      ...updatedStayDetails[index],
      [field]: value,
    };
    setStay(updatedStayDetails);
  };

  const handleSelectChange = (
    index: number,
    event: SelectChangeEvent<string>
  ): void => {
    const updatedStayDetails = [...stayDetails];
    updatedStayDetails[index] = {
      ...updatedStayDetails[index],
      accomodationType: event.target.value as AccommodationType | "",
    };
    setStay(updatedStayDetails);
  };

  const handleAmitiesChange = (
    index: number,
    event: SelectChangeEvent<string[]>
  ): void => {
    const updatedStayDetails = [...stayDetails];
    const value = event.target.value;

    updatedStayDetails[index] = {
      ...updatedStayDetails[index],
      amities: typeof value === "string" ? value.split(",") : value,
    };

    setStay(updatedStayDetails);
  };

  const handleLocationChange = (
    index: number,
    field: "district" | "state",
    value: string
  ): void => {
    const updatedStayDetails = [...stayDetails];

    if (field === "state") {
      updatedStayDetails[index] = {
        ...updatedStayDetails[index],
        state: value,
        district: "",
      };
    } else {
      updatedStayDetails[index] = {
        ...updatedStayDetails[index],
        [field]: value,
      };
    }

    setStay(updatedStayDetails);
  };

  const addImages = async (index: number, images: Image[]): Promise<void> => {
    setStay((prevStayDetails) => {
      const updatedStayDetails = [...prevStayDetails];
      updatedStayDetails[index].hotelImage = images;
      return updatedStayDetails;
    });
  };

  const handleUpload = async (index: number) => {
    const images = stayDetails[index].hotelImage;

    if (images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }
    if (images.length < 3) {
      toast.error("Add at least 3 images");
      return;
    }
    const files = images.map((image) => ({
      file: image.file as File,
      folderName: folderName,
      fileName: image.fileName,
      mimetype: image.mimetype,
    })) as any;

    const response = await dispatch(uploadMultipleImages({ files }) as any);

    if (response.meta.requestStatus === "fulfilled") {
      toast.success("Image uploaded successfully");

      setStay((prevStayDetails) => {
        const updatedStayDetails = [...prevStayDetails];
        updatedStayDetails[index].hotelImage = response.payload;
        return updatedStayDetails;
      });
    } else {
      toast.error("Failed to upload cover image");
      setStay((prevStayDetails) => {
        const updatedStayDetails = [...prevStayDetails];
        updatedStayDetails[index].hotelImage = [];
        return updatedStayDetails;
      });
    }
  };

  const handleConfigModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setConfigMode(event.target.value as "addDetails" | "saveAs");

    if (event.target.value === "addDetails") {
      setSelectedNightSource("");
    }
  };

  const handleNightSourceChange = (event: SelectChangeEvent): void => {
    setSelectedNightSource(event.target.value);
  };

  const applyNightDetails = (index: number): void => {
    if (configMode === "saveAs" && selectedNightSource !== "") {
      const sourceNight = stayDetails.find(
        (night) => night.id === selectedNightSource
      );

      if (sourceNight) {
        const updatedStayDetails = [...stayDetails];

        // Just store reference to the source night
        updatedStayDetails[index] = {
          ...updatedStayDetails[index],
          sameAs: sourceNight.id,
        };

        setStay(updatedStayDetails);
      }
    }

    // Reset the selection state
    setConfigMode("");
    setSelectedNightSource("");
  };

  const hasValidImages = (night: NightDetails): boolean => {
    // If it's linked to another night, check that one
    if (night.sameAs) {
      const sourceNight = stayDetails.find((n) => n.id === night.sameAs);
      return sourceNight ? sourceNight.hotelImage.length > 0 : false;
    }

    // Otherwise check its own images
    return night.hotelImage.length > 0;
  };

  const getNightData = (night: NightDetails): NightDetails => {
    if (night.sameAs) {
      const sourceNight = stayDetails.find((n) => n.id === night.sameAs);
      return sourceNight || night;
    }
    return night;
  };

  const handleSave = async (): Promise<void> => {
    if (onSave) {
      try {
        // Process images if needed before saving
        const processedStayDetails = await Promise.all(
          stayDetails.map(async (night) => {
            // If it's a linked night, just return it as is
            if (night.sameAs) return night;

            // For a real implementation, you would process/upload the images here
            // This is just a simulation
            await new Promise((resolve) => setTimeout(resolve, 500));

            return night;
          })
        );

        await onSave(processedStayDetails, isNotFinalized);
        handleClose();
      } catch (error) {
        console.error("Error saving stay details:", error);
      }
    } else {
      handleClose();
    }
  };

  const isFormValid = (): boolean => {
    if (isNotFinalized) return true;

    if (stayDetails.length === 0) return false;

    // Make sure ALL nights are configured
    if (stayDetails.length < nights) return false;

    return stayDetails.every((night) => {
      // If it's a linked night, no need to validate its own fields
      if (night.sameAs) {
        const sourceNight = stayDetails.find((n) => n.id === night.sameAs);
        return sourceNight ? isNightValid(sourceNight) : false;
      }

      return isNightValid(night);
    });
  };

  const isNightValid = (night: NightDetails): boolean => {
    return Boolean(
      night.accomodationType &&
        night.hotelName &&
        night.district &&
        night.state &&
        hasValidImages(night)
    );
  };

  const getNightDisplayName = (index: number, night: NightDetails): string => {
    const actualNight = getNightData(night);
    return `Night ${index + 1}${
      actualNight.hotelName ? ` - ${actualNight.hotelName}` : ""
    }${
      actualNight.accomodationType ? ` (${actualNight.accomodationType})` : ""
    }`;
  };

  // Get source night index for display
  const getSourceNightIndex = (sourceId: string): number => {
    return stayDetails.findIndex((night) => night.id === sourceId);
  };

  return (
    <Card sx={{ mx: "auto", my: 2 }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Stay Details
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNotFinalized}
                  onChange={() => setNoStay(!isNotFinalized)}
                />
              }
              label="Property details not yet finalized.  (Please select this option if you want to update trip details and add the stay details later.)"
            />
            {isNotFinalized && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 1,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  backgroundColor: "#e3f2fd",
                }}
              >
                <InfoIcon sx={{ color: "#1565c0" }} />
                <Typography variant="body2" sx={{ color: "#1565c0" }}>
                  The property details are not yet finalized. You can save this
                  state without adding specific night details.
                </Typography>
              </Paper>
            )}
          </Box>

          {!isNotFinalized && (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  marginBottom: 2,
                }}
              >
                Add accommodation details for your trip
              </Typography>

              {stayDetails.length !== 0 &&
                stayDetails.map((night, index) => {
                  const isLinkedNight = Boolean(night.sameAs);
                  const displayData = getNightData(night);
                  const sourceNightIndex = isLinkedNight
                    ? getSourceNightIndex(night.sameAs as string)
                    : -1;

                  return (
                    <Accordion
                      key={night.id}
                      expanded={expanded === `panel${index}`}
                      onChange={handleAccordionChange(`panel${index}`)}
                      sx={{
                        marginBottom: 2,
                        borderRadius: 1,
                        ...(isLinkedNight && {
                          border: "1px dashed #90caf9",
                          backgroundColor: "#fafafa",
                        }),
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            margin: "12px 0",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            pr: 2,
                          }}
                        >
                          {isLinkedNight && (
                            <LinkIcon
                              fontSize="small"
                              sx={{
                                color: "#1976d2",
                                mr: 1,
                              }}
                            />
                          )}
                          <Typography
                            sx={{ fontWeight: 500, marginRight: "auto" }}
                          >
                            {getNightDisplayName(index, night)}
                            {isLinkedNight && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 1,
                                  color: "#1976d2",
                                }}
                              >
                                (Same as Night {sourceNightIndex + 1})
                              </Box>
                            )}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            {displayData.hotelName && hasValidImages(night) && (
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{ color: "#4caf50" }}
                              />
                            )}
                            {isLinkedNight && (
                              <Tooltip title="Break link and edit separately">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBreakLink(index);
                                  }}
                                  size="small"
                                  sx={{ color: "#1976d2" }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveNight(index);
                              }}
                              size="small"
                              sx={{ color: "#f44336" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails sx={{ padding: 2 }}>
                        {isLinkedNight ? (
                          <Box sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                color: "#666",
                              }}
                            >
                              This night uses the same details as Night{" "}
                              {sourceNightIndex + 1}. Any changes to Night{" "}
                              {sourceNightIndex + 1} will also apply here.
                            </Typography>
                            <Button
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleBreakLink(index)}
                              size="small"
                            >
                              Break Link & Edit Separately
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ position: "relative", pb: 2 }}>
                            {stayDetails.length > 1 && (
                              <Box
                                sx={{
                                  mb: 3,
                                  pb: 2,
                                  borderBottom: "1px solid #e0e0e0",
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Night Configuration
                                </Typography>
                                <RadioGroup
                                  row
                                  value={configMode}
                                  onChange={handleConfigModeChange}
                                >
                                  <FormControlLabel
                                    value="addDetails"
                                    control={<Radio />}
                                    label="Add Details"
                                  />
                                  <FormControlLabel
                                    value="saveAs"
                                    control={<Radio />}
                                    label="Use Same As Another Night"
                                  />
                                </RadioGroup>

                                {configMode === "saveAs" && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <FormControl sx={{ minWidth: 250 }}>
                                      <InputLabel
                                        id={`copy-from-label-${index}`}
                                      >
                                        Select Night
                                      </InputLabel>
                                      <Select
                                        labelId={`copy-from-label-${index}`}
                                        id={`copy-from-select-${index}`}
                                        value={selectedNightSource}
                                        label="Select Night"
                                        onChange={handleNightSourceChange}
                                        size="small"
                                        startAdornment={
                                          <ContentCopyIcon
                                            fontSize="small"
                                            sx={{
                                              mr: 1,
                                              color: "#666",
                                            }}
                                          />
                                        }
                                      >
                                        <MenuItem value="" disabled>
                                          <em>Select night</em>
                                        </MenuItem>
                                        {stayDetails.map(
                                          (otherNight, idx) =>
                                            idx !== index &&
                                            !otherNight.sameAs && (
                                              <MenuItem
                                                key={otherNight.id}
                                                value={otherNight.id}
                                              >
                                                Night {idx + 1} -{" "}
                                                {otherNight.hotelName ||
                                                  "Unnamed"}
                                              </MenuItem>
                                            )
                                        )}
                                      </Select>
                                    </FormControl>

                                    {selectedNightSource !== "" && (
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => applyNightDetails(index)}
                                      >
                                        Apply
                                      </Button>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )}

                            {(configMode !== "saveAs" ||
                              stayDetails.length <= 1) && (
                              <Grid container spacing={2}>
                                {/* Basic Info */}
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth disabled={!editable}>
                                    <InputLabel>
                                      Accommodation Type *
                                    </InputLabel>
                                    <Select
                                      value={night.accomodationType}
                                      label="Accommodation Type *"
                                      onChange={(e) =>
                                        handleSelectChange(index, e)
                                      }
                                    >
                                      {ACCOMMODATION_TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Property Title *"
                                    placeholder="Enter property title"
                                    value={night.hotelName}
                                    onChange={(e) =>
                                      handleTextFieldChange(
                                        index,
                                        "hotelName",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    disabled={!editable}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth margin="normal">
                                    <InputLabel>State</InputLabel>
                                    <Select
                                      value={stayDetails[index].state}
                                      onChange={(e) =>
                                        handleLocationChange(
                                          index,
                                          "state",
                                          e.target.value
                                        )
                                      }
                                      label="State"
                                      disabled={!editable}
                                    >
                                      {CountryStateDistrict.getAllStates().map(
                                        (state) => (
                                          <MenuItem
                                            key={state.id}
                                            value={state.name}
                                          >
                                            {state.name}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <FormControl
                                    fullWidth
                                    margin="normal"
                                    disabled={!stayDetails[index].state}
                                  >
                                    <InputLabel>District</InputLabel>
                                    <Select
                                      value={stayDetails[index].district}
                                      onChange={(e) =>
                                        handleLocationChange(
                                          index,
                                          "district",
                                          e.target.value
                                        )
                                      }
                                      label="District"
                                      disabled={!editable}
                                    >
                                      {CountryStateDistrict.getDistrictsByStateName(
                                        stayDetails[index].state
                                      ).map((district) => (
                                        <MenuItem
                                          key={district}
                                          value={district}
                                        >
                                          {district}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                {/* Hotel Introduction */}
                                <Grid item xs={12}>
                                  <TextField
                                    label="Hotel Introduction"
                                    placeholder="Brief description of the accommodation (up to 500 characters)"
                                    value={night.hotelIntro}
                                    onChange={(e) =>
                                      handleTextFieldChange(
                                        index,
                                        "hotelIntro",
                                        e.target.value
                                      )
                                    }
                                    disabled={!editable}
                                    multiline
                                    rows={3}
                                    fullWidth
                                    inputProps={{ maxLength: 500 }}
                                    helperText={`${night.hotelIntro.length}/500 characters`}
                                  />
                                </Grid>

                                {/* Amenities Selection */}
                                <Grid item xs={12}>
                                  <FormControl fullWidth disabled={!editable}>
                                    <InputLabel id={`amities-label-${index}`}>
                                      Amenities
                                    </InputLabel>
                                    <Select
                                      labelId={`amities-label-${index}`}
                                      id={`amities-select-${index}`}
                                      multiple
                                      value={night.amities}
                                      onChange={(e) =>
                                        handleAmitiesChange(index, e)
                                      }
                                      input={
                                        <OutlinedInput label="Amenities" />
                                      }
                                      renderValue={(selected) => (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                          }}
                                        >
                                          {selected.map((value) => (
                                            <Chip
                                              key={value}
                                              label={value}
                                              size="small"
                                            />
                                          ))}
                                        </Box>
                                      )}
                                      MenuProps={{
                                        PaperProps: {
                                          style: {
                                            maxHeight: 300,
                                          },
                                        },
                                      }}
                                    >
                                      {AMITIES.map((amity) => (
                                        <MenuItem key={amity} value={amity}>
                                          <Checkbox
                                            checked={
                                              night.amities.indexOf(amity) > -1
                                            }
                                          />
                                          {amity}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                {/* Images Section */}
                                <Grid item xs={12}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                    Images *
                                  </Typography>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    {imageUploadLoading[index] ? (
                                      <Box
                                        sx={{
                                          height: 80,
                                          width: 80,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          border: "1px dashed #e0e0e0",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : (
                                      <ImageUploader
                                        images={night.hotelImage}
                                        setImages={(img: any) =>
                                          addImages(index, img)
                                        }
                                        maxFileSize={150 * 1024} //make it 150kb
                                        label={`Upload Night ${
                                          index + 1
                                        } Images`}
                                        handleUpload={() => handleUpload(index)}
                                        maxFiles={10}
                                        sequence
                                        appendFileName={`Night-${index + 1}`}
                                        disabled={!editable}
                                        readOnly={!editable}
                                        allowedAspectRatios={[9 / 16]}
                                        compress={false}
                                      />
                                    )}
                                  </Box>

                                  {night.hotelImage.length === 0 && (
                                    <FormHelperText error>
                                      Add at least 3 images
                                    </FormHelperText>
                                  )}
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              {stayDetails.length < nights && !isNotFinalized && (
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  onClick={handleAddNight}
                  sx={{ mt: 2, mb: 2 }}
                  disabled={!editable}
                >
                  Add Night {stayDetails.length + 1}
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            {!isNotFinalized && stayDetails.length > 0 && (
              <Typography variant="caption" sx={{ color: "#666" }}>
                {stayDetails.length} of {nights} nights configured
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                (stayDetails.length === 0 && !isNotFinalized) ||
                (!isNotFinalized && !isFormValid()) ||
                saving ||
                !editable
              }
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                minWidth: "100px",
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#666",
                },
              }}
            >
              {saving ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Saving...
                </Box>
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StayDetailsModal;
